import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Truck, Loader2, ExternalLink, CheckCircle, Gift, Mail, MessageCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;
const WHATSAPP_NUMBER = '491727012056';
const SUPPORT_EMAIL = 'support@allflowx.de';

export const LieferungAddonCard = ({ showOnlyWhenInactive = false }) => {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);

  const isPreviouslyRegistered = user?.previously_registered === true;

  const fetchStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/addon/lieferung/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(res.data);
    } catch {
      setStatus({ status: 'inactive' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleCheckout = async () => {
    setCheckoutLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/api/addon/lieferung/checkout`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Checkout fehlgeschlagen');
      setCheckoutLoading(false);
    }
  };

  const handlePortal = async () => {
    setPortalLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/addon/stripe-portal`, {
        headers: { Authorization: `Bearer ${token}`, origin: window.location.origin }
      });
      window.location.href = res.data.url;
    } catch {
      toast.error('Kundenportal konnte nicht geöffnet werden');
      setPortalLoading(false);
    }
  };

  if (loading) return null;

  const addonStatus = status?.status;

  // V2-Kunden: Lieferung ist normales Paketmodul — Add-on-Karte nicht anzeigen
  if (addonStatus === 'included_in_package') {
    return null;
  }

  // Im Dashboard-Modus: nur anzeigen wenn Addon nicht aktiv ist
  if (showOnlyWhenInactive && addonStatus && addonStatus !== 'inactive') {
    return null;
  }

  // Bereits aktiv (trial oder active) → kompakte Status-Anzeige
  if (addonStatus === 'active' || addonStatus === 'trial') {
    const daysLeft = status?.days_left ?? null;
    const isUrgent = addonStatus === 'trial' && daysLeft !== null && daysLeft <= 30;

    return (
      <Card
        className={`${isUrgent
          ? 'border-orange-300 dark:border-orange-700 bg-orange-50/40 dark:bg-orange-950/10'
          : 'border-green-200 dark:border-green-800 bg-green-50/40 dark:bg-green-950/10'}`}
        data-testid="addon-active-card"
      >
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${isUrgent ? 'bg-orange-100 dark:bg-orange-900/40' : 'bg-green-100 dark:bg-green-900/40'}`}>
                <Truck className={`h-4 w-4 ${isUrgent ? 'text-orange-600 dark:text-orange-400' : 'text-green-600 dark:text-green-400'}`} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-sm">Lieferschein-KI-Manager</p>
                  {isUrgent ? (
                    <Badge className="bg-orange-500 text-white text-xs animate-pulse">
                      Endet in {daysLeft} {daysLeft === 1 ? 'Tag' : 'Tagen'}
                    </Badge>
                  ) : (
                    <Badge className="bg-green-600 text-white text-xs">Aktiv</Badge>
                  )}
                </div>
                {addonStatus === 'trial' && status?.trial_end_formatted && (
                  <p className={`text-xs ${isUrgent ? 'text-orange-700 dark:text-orange-300 font-medium' : 'text-muted-foreground'}`}>
                    Kostenlos bis <strong>{status.trial_end_formatted}</strong> · Danach 1,99&nbsp;€ Netto/Monat
                  </p>
                )}
              </div>
            </div>
            {(
              <Button
                size="sm"
                variant={isUrgent ? 'default' : 'outline'}
                onClick={handlePortal}
                disabled={portalLoading}
                className={isUrgent ? 'bg-orange-500 hover:bg-orange-600 text-white' : ''}
                data-testid="addon-manage-btn"
              >
                {portalLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <ExternalLink className="h-3 w-3 mr-1" />}
                {isUrgent ? 'Jetzt verlängern' : 'Abo verwalten'}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Nicht aktiv → Upsell-Karte
  return (
    <Card className="border-blue-200 dark:border-blue-800 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20" data-testid="addon-upsell-card">
      <CardContent className="p-5">
        <div className="flex flex-col sm:flex-row gap-4 items-start">
          {/* Icon */}
          <div className="p-3 bg-blue-100 dark:bg-blue-900/40 rounded-xl flex-shrink-0">
            <Truck className="h-6 w-6 text-blue-600 dark:text-blue-400" />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h3 className="font-bold text-base">Lieferschein-KI-Manager</h3>
              {!isPreviouslyRegistered && (
                <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 border-0 text-xs">
                  <Gift className="h-3 w-3 mr-1" />
                  6 Monate GRATIS
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground mb-3">
              KI-Import via E-Mail · 100% papierlos · rechtssicher archiviert · Abweichungen sofort erkennen
            </p>

            {/* Pricing info */}
            <div className="flex items-center gap-4 mb-4 flex-wrap">
              {!isPreviouslyRegistered && (
                <div className="flex items-center gap-1.5">
                  <CheckCircle className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
                  <span className="text-xs font-medium">6 Monate kostenlos</span>
                </div>
              )}
              <div className="flex items-center gap-1.5">
                <CheckCircle className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
                <span className="text-xs font-medium">
                  {isPreviouslyRegistered ? '1,99\u00a0€ Netto/Monat' : 'Danach: 1,99\u00a0€ Netto/Monat'}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <CheckCircle className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
                <span className="text-xs font-medium">Jederzeit kündbar</span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap items-center gap-3">
              <Button
                onClick={handleCheckout}
                disabled={checkoutLoading}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                size="sm"
                data-testid="addon-checkout-btn"
              >
                {checkoutLoading
                  ? <><Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />Wird geladen…</>
                  : isPreviouslyRegistered ? 'Jetzt buchen' : 'Jetzt kostenlos starten'}
              </Button>

              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span>Hilfe?</span>
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="flex items-center gap-1 hover:text-foreground transition-colors"
                  data-testid="addon-email-support"
                >
                  <Mail className="h-3 w-3" />
                  {SUPPORT_EMAIL}
                </a>
                <span>·</span>
                <a
                  href={`https://wa.me/${WHATSAPP_NUMBER}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 hover:text-green-600 transition-colors"
                  data-testid="addon-whatsapp-support"
                >
                  <MessageCircle className="h-3 w-3" />
                  WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default LieferungAddonCard;
